import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { workspaceActions, workspaceSelectors, workspaceTypes } from 'redux/workspace';
import Grid from 'components/Grid';
import Avatar from 'components/Avatar';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Fade from '@mui/material/Fade';
import Popper from '@mui/material/Popper';
import styled from '@emotion/styled/macro';
import { isMobile } from 'helpers';
import useReduxLoading from 'hooks/useReduxLoading';
import TextLoader from 'components/TextLoader';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import EmptyState from 'components/EmptyState/EmptyState';
import { useTranslation } from 'react-i18next';
import MemberQuickActions from './Member/QuickActions/MemberQuickActions';

const StyledPopper = styled(Popper)`
  z-index: ${({ theme }) => theme.zIndex.overflowMenu};

  padding-top: 0.25rem; // WHy?
`;

const Name = styled('div')`
  font-weight: 500;
`;

const Subtitle = styled('div')`
  font-size: 0.8rem;
  color: #888;

  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const StyledCard = styled(Card)`
  min-width: 250px;
  max-width: 300px;

  width: 275px;
`;

const StyledCardContent = styled(CardContent)`
  border-bottom: 1px solid #eee;
`;

const MemberContent = React.forwardRef(({ memberId, ...rest }, ref) => {

  const { t } = useTranslation(['common']);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(workspaceActions.getMember({ memberId }));
  }, [dispatch, memberId]);

  const member = useSelector(workspaceSelectors.getMemberSelector)(memberId);

  const loading = useReduxLoading(
    [workspaceTypes.getMember],
    [workspaceTypes.getMemberSuccess, workspaceTypes.getMemberFailure],
    !member?.id,
    memberId,
  );

  return (
    <StyledCard ref={ref} raised {...rest}>
      <StyledCardContent>
        {loading && <TextLoader />}

        {!loading && !member?.id && (
          <Grid container direction="column" alignItems="center">
            <Grid item>
              <EmptyState sx={{ maxWidth: 150 }} subtitle={t('memberPopover.failedToFetch')} />
            </Grid>
          </Grid>
        )}

        {!loading && member?.id && (
          <Grid container direction="row" alignItems="center" wrap="nowrap" spacing={2}>
            <Grid item>
              <Avatar size={32} src={member?.avatar?.src} name={member?.name} />
            </Grid>
            <Grid item xs zeroMinWidth>
              <Grid container direction="column" wrap="nowrap" spacing={0.5}>
                <Grid item xs zeroMinWidth><Name>{member?.name}</Name></Grid>
                {member?.job && <Grid item xs zeroMinWidth><Subtitle>{member?.job}</Subtitle></Grid>}
              </Grid>
            </Grid>
          </Grid>
        )}
      </StyledCardContent>

      {!loading && member?.id && (
        <CardActions>
          <MemberQuickActions
            contactNumber={member?.contactNumber}
            contactEmail={member?.contactEmail}
            profileMemberId={memberId}
            chatMemberId={member?.permissions?.chat ? memberId : null}

            edge={null}
          />
        </CardActions>
      )}
    </StyledCard>
  );
});

MemberContent.propTypes = {
  memberId: PropTypes.any,
};

const popoverDelay = 500;
const MemberPopover = ({ memberId, popperProps = {}, children, ...rest }) => {

  const anchorEl = useRef();
  const [open, setOpen] = useState(false);

  const timeoutRef = useRef();

  if (!memberId) {
    return <Box {...rest}>{children}</Box>;
  }

  const onMouseEnter = () => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setOpen(true), popoverDelay);
  };

  const onMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setOpen(false);
  };

  const onClick = (e) => {
    if (!isMobile) {
      return;
    }

    // On mobile. Initial click. Open the popover and ignore everything else
    if (!open) {
      setOpen(true);
      e.preventDefault();
      e.stopPropagation();
    }

    setOpen(false);
    // Proceed with default onClick action if one is set outside of this component.
  };

  return (
    <Box
      ref={anchorEl}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}

      {...rest}
    >
      {children}

      <ClickAwayListener onClickAway={onMouseLeave}>
        <StyledPopper open={open} anchorEl={anchorEl.current} transition placement="bottom-start" {...popperProps}>
          {({ TransitionProps }) => (
            <Fade {...TransitionProps}>
              <MemberContent memberId={memberId} />
            </Fade>
          )}
        </StyledPopper>
      </ClickAwayListener>
    </Box>
  );
};

MemberPopover.propTypes = {
  memberId: PropTypes.any,

  popperProps: PropTypes.object,
};

export default MemberPopover;
